/**
 * AddMemberSheet — entry sheet for the "Add student" affordances on
 * /my-family (Plan 06). (Guardian invites open `<InviteCoParentSheet />`
 * directly via the Guardians card's "+ Add guardian".)
 *
 * Routes between a few screens, then delegates to a sub-sheet:
 *
 *   - `picker` — pick student vs co-owner (legacy generic entry; no live
 *     affordance opens it today).
 *   - `student-method` — the "Add student" chooser: "Use a school code"
 *     (bulk-claim enrolled students) vs "Add manually" (the single-student
 *     form). This is the entry point every "Add student" button lands on.
 *   - `student-code` / `student` / `parent` — the leaf sub-sheets.
 *
 * The sub-sheets reuse the same `open` + `onOpenChange` so only one is
 * visible at a time; a "Back" button on each returns to `student-method`.
 *
 * Per Plan 06 the pickers use Ordo UI blue primary buttons — not green.
 */

import { Card } from '@ordo/ui/card'
import type { SearchableSelectOption } from '@ordo/ui/searchable-select'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@ordo/ui/sheet'
import { GraduationCap, KeyRound, Users } from 'lucide-react'
import { useEffect, useState } from 'react'

import { AddParentSheet } from './add-parent-sheet'
import { AddStudentSheet } from './add-student-sheet'
import { ClaimWithCodeSheet } from './claim-with-code-sheet'

/**
 * `picker`         — the "Add a member" chooser (student vs co-owner).
 * `student-method` — the "Add student" chooser (school code vs manual).
 * `student-code`   — bulk-claim enrolled students with an activation code.
 * `student`        — the manual single-student form.
 * `parent`         — invite a co-owner adult.
 */
type Mode = 'picker' | 'student-method' | 'student-code' | 'student' | 'parent'

export interface AddMemberSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  allergenOptions: SearchableSelectOption[]
  dietaryOptions: SearchableSelectOption[]
  onAdded?: () => void
  /** Claimed student user ids from the school-code branch — the caller
   *  opens the complete-student-info sheet for any with missing facts. */
  onClaimedStudents?: (studentUserIds: string[]) => void
  /** Which view to show when the sheet opens (default the kind picker).
   *  Pass `'student-method'` to jump straight to the Add Student chooser. */
  initialMode?: Mode
}

export function AddMemberSheet({
  open,
  onOpenChange,
  allergenOptions,
  dietaryOptions,
  onAdded,
  onClaimedStudents,
  initialMode = 'picker',
}: AddMemberSheetProps) {
  const [mode, setMode] = useState<Mode>(initialMode)

  // Reset to the caller's initial view each time the sheet (re)opens, so a
  // trigger that wants the student form lands there while a generic "+ Add
  // member" still gets the picker. Internal picker→sub-sheet navigation
  // doesn't re-run this (only `open`/`initialMode` changes do).
  useEffect(() => {
    if (open) {
      setMode(initialMode)
    }
  }, [open, initialMode])

  function close() {
    setMode(initialMode)
    onOpenChange(false)
  }

  return (
    <>
      <Sheet
        open={open && mode === 'picker'}
        onOpenChange={(next) => {
          if (!next) {
            close()
          }
        }}>
        <SheetContent side="right" size="md" className="flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 pb-4 pt-6">
            <SheetTitle>Add a member</SheetTitle>
            <SheetDescription>Add a student to this household or invite another adult as a co-owner.</SheetDescription>
          </SheetHeader>
          <div className="grid gap-3 px-6 py-4">
            <KindCard
              icon={<GraduationCap className="h-5 w-5" aria-hidden />}
              title="Add student"
              description="Most students don't need their own sign-in — you'll place orders for them."
              onClick={() => setMode('student-method')}
            />
            <KindCard
              icon={<Users className="h-5 w-5" aria-hidden />}
              title="Invite a co-owner"
              description="Add another adult to this household. They'll manage students + orders alongside you."
              onClick={() => setMode('parent')}
            />
          </div>
        </SheetContent>
      </Sheet>

      <Sheet
        open={open && mode === 'student-method'}
        onOpenChange={(next) => {
          if (!next) {
            close()
          }
        }}>
        <SheetContent side="right" size="md" className="flex flex-col gap-0 p-0">
          <SheetHeader className="px-6 pb-4 pt-6">
            <SheetTitle>Add student</SheetTitle>
            <SheetDescription>Choose how to add this student to your household.</SheetDescription>
          </SheetHeader>
          <div className="grid gap-3 px-6 py-4">
            <KindCard
              icon={<KeyRound className="h-5 w-5" aria-hidden />}
              title="Use a school code"
              description="Have an activation code from your school? Load the students already added for you."
              onClick={() => setMode('student-code')}
            />
            <KindCard
              icon={<GraduationCap className="h-5 w-5" aria-hidden />}
              title="Add manually"
              description="Enter a student's details yourself — name, school, allergens."
              onClick={() => setMode('student')}
            />
          </div>
        </SheetContent>
      </Sheet>

      <ClaimWithCodeSheet
        open={open && mode === 'student-code'}
        onOpenChange={(next) => {
          if (!next) {
            close()
          }
        }}
        onClaimed={() => {
          onAdded?.()
        }}
        onClaimedStudents={onClaimedStudents}
        onBack={() => setMode('student-method')}
      />

      <AddStudentSheet
        open={open && mode === 'student'}
        onOpenChange={(next) => {
          if (!next) {
            close()
          }
        }}
        allergenOptions={allergenOptions}
        dietaryOptions={dietaryOptions}
        onAdded={() => {
          onAdded?.()
          close()
        }}
        onBack={() => setMode('student-method')}
      />

      <AddParentSheet
        open={open && mode === 'parent'}
        onOpenChange={(next) => {
          if (!next) {
            close()
          }
        }}
        onInvited={() => {
          onAdded?.()
          close()
        }}
      />
    </>
  )
}

interface KindCardProps {
  icon: React.ReactNode
  title: string
  description: string
  onClick: () => void
}

function KindCard({ icon, title, description, onClick }: KindCardProps) {
  return (
    <Card
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className="flex cursor-pointer items-start gap-3 p-4 text-left transition-colors hover:bg-accent/40 focus:outline-none focus:ring-2 focus:ring-ring">
      <div className="rounded-md bg-primary/10 p-2 text-primary">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-foreground">{title}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
    </Card>
  )
}
